import { yupResolver } from "@hookform/resolvers/yup"
import { Button, FormLabel, Modal, Select, TextArea } from "@oysterhr/ui"
import { useMemo } from "react"
import { Controller, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import * as yup from "yup"
import { TodoTasksTaskCategory, Engagement } from "@oysterhr/schema"
import { useCreateNote } from "../hooks/useCreateNote"
import { SortedCategoriesEnum } from "../utils"
import { TodoTasksTaskDetailsFragment } from "../../../gql/graphql"

const schema = yup.object({
  category: yup.string().required("Category is required"),
  task: yup.string().required("Task is required"),
  content: yup.string().trim().required("Content is required"),
})

type NewNoteValues = {
  category: TodoTasksTaskCategory
  task: string
  content: string
}

type CreateNoteModalProps = {
  tasks: TodoTasksTaskDetailsFragment[]
  isOpen: boolean
  onRequestClose: () => void
  hiringId: string
  teamMemberName: string
  companyName: string
  isPayrollTM?: Engagement["isPayrollTeamMember"]
}

export function CreateNoteModal({
  tasks,
  isOpen,
  onRequestClose,
  hiringId,
  teamMemberName,
  companyName,
  isPayrollTM,
}: CreateNoteModalProps) {
  const [t] = useTranslation("staff_interface/TabProcessOverview")

  // Mutation
  const { createNote, loading } = useCreateNote()

  // Form
  const {
    handleSubmit,
    control,
    register,
    watch,
    setValue,
    formState: { errors },
  } = useForm<NewNoteValues>({
    resolver: yupResolver(schema),
  })
  const selectedCategory = watch("category")

  async function onSubmit(values: NewNoteValues) {
    await createNote(hiringId, values.task, values.content)
    onRequestClose()
  }

  const categories = useMemo(() => {
    const allSortedCategories = Object.values(SortedCategoriesEnum)
    const existingTasksCategories = tasks.map(task => task.category)

    // Only display categories for existing tasks
    const categoriesForExistingTasks = allSortedCategories.filter(category =>
      existingTasksCategories.includes(category),
    )
    return categoriesForExistingTasks.map(category => ({
      value: category,
      label: t(`categories.${category}.title`, {
        teamMemberName,
        companyName,
      }),
    }))
  }, [t, teamMemberName, companyName, tasks])
  const categoryTasks = useMemo(() => {
    return tasks.filter(task => task.category === selectedCategory)
  }, [tasks, selectedCategory])

  return (
    <Modal isOpen={isOpen} onRequestClose={onRequestClose}>
      <Modal.Header>{t("addNewNoteModal.title")}</Modal.Header>
      <Modal.Content>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="flex flex-col space-y-3">
            <Controller
              name="category"
              control={control}
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <Select
                  id="select-process-note-category"
                  label={
                    <FormLabel
                      label={
                        isPayrollTM
                          ? t("ptmAddNewNoteModal.blockLabel")
                          : t("addNewNoteModal.blockLabel")
                      }
                    />
                  }
                  placeholder={t("addNewNoteModal.blockPlaceholder")}
                  value={value}
                  onChange={value => {
                    onChange(value)
                    // Reset task value when category changes
                    if (selectedCategory !== value) {
                      setValue("task", "")
                    }
                  }}
                  error={error?.message}
                >
                  {categories.map(category => (
                    <Select.Option key={category.value} value={category.value}>
                      {category.label}
                    </Select.Option>
                  ))}
                </Select>
              )}
            />

            <Controller
              name="task"
              control={control}
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <Select
                  id="select-process-note-task"
                  disabled={!selectedCategory}
                  label={
                    <FormLabel
                      label={
                        isPayrollTM
                          ? t("ptmAddNewNoteModal.stepLabel")
                          : t("addNewNoteModal.stepLabel")
                      }
                      assistiveText={t("addNewNoteModal.stepAssistiveText")}
                    />
                  }
                  placeholder={t("addNewNoteModal.stepPlaceholder")}
                  value={value}
                  onChange={onChange}
                  error={error?.message}
                >
                  {categoryTasks.map(task => (
                    <Select.Option key={task.id} value={task.name}>
                      {isPayrollTM
                        ? t([`ptmTasks.${task.name}`, `tasks.${task.name}`])
                        : t(`tasks.${task.name}`)}
                    </Select.Option>
                  ))}
                </Select>
              )}
            />

            <TextArea
              label={<FormLabel label={t("addNewNoteModal.noteLabel")} />}
              placeholder={t("addNewNoteModal.notePlaceholder")}
              {...register("content")}
              error={errors.content?.message}
            />
          </div>
          <div className="mt-4 flex flex-row items-center justify-end space-x-3">
            <Button className="w-fit" variation="outline" onClick={onRequestClose}>
              {t("addNewNoteModal.cancel")}
            </Button>
            <Button className="w-fit" isLoading={loading} type="submit">
              {t("addNewNoteModal.save")}
            </Button>
          </div>
        </form>
      </Modal.Content>
    </Modal>
  )
}
